import type { ScratchPadStroke, ScratchPadTextItem } from "./types";

export interface CropBox {
  x: number;
  y: number;
  size: number;
}

const PADDING = 28;
const MIN_SIZE = 96;
/** Height of a painted text box. The crop must agree with what is drawn. */
export const TEXT_BOX_HEIGHT = 120;

/**
 * The square region of the page worth showing as a thumbnail.
 *
 * A scratchpad page is mostly blank, so a thumbnail of the whole thing reads
 * as an empty square. This finds what the student actually put down, pads it,
 * and squares it off so a cover-fit crop shaves nothing. Null when the page
 * holds nothing worth showing.
 */
export const contentCropBox = (
  strokes: readonly ScratchPadStroke[],
  textItems: readonly ScratchPadTextItem[],
  width: number,
  height: number,
): CropBox | null => {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  const include = (x: number, y: number) => {
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
  };

  strokes.forEach((stroke) =>
    stroke.points.forEach(([px, py]) => include(px * width, py * height)),
  );
  textItems.forEach((item) => {
    if (!item.text.trim()) return;
    include(item.x * width, item.y * height);
    include(item.x * width + item.w * width, item.y * height + TEXT_BOX_HEIGHT);
  });

  if (minX === Infinity) return null;

  const span = Math.max(maxX - minX, maxY - minY) + PADDING * 2;
  // The floor applies first, then the page: a page smaller than the floor
  // still gets a box that fits inside it rather than one that overruns.
  const size = Math.min(Math.max(MIN_SIZE, span), width, height);
  const centreX = (minX + maxX) / 2;
  const centreY = (minY + maxY) / 2;

  return {
    x: Math.max(0, Math.min(width - size, centreX - size / 2)),
    y: Math.max(0, Math.min(height - size, centreY - size / 2)),
    size,
  };
};
